import {useEffect, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import FlightIcon from "@mui/icons-material/Flight";
import PrintIcon from "@mui/icons-material/Print";
import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import RosterLineup from "@/components/RosterLineup";
import VLFSelectNew from "@/components/Input/SelectNew";
import {ContractAPI} from "@/api/contract";
import {MatchAPI} from "@/api/match";
import {ModuleAPI} from "@/api/module";
import {MedicalAPI} from "@/api/medical";
import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";
import {TrainingAPI} from "@/api/training";
import {
	getPlayerDetailPath,
	isInternalPlayer,
	visibleRosters,
} from "@/utils/functions";
import {getInternalClubId} from "@/utils/constants";
import {COLORS} from "@/style/utils";
import {
	matchSide,
	rosterMatches,
	rosterTrainings,
	readinessForPlayers,
	trainingSummary,
	minutesSummary,
} from "./data";
import "./index.scss";

const number = (value) =>
	value == null
		? "—"
		: value.toLocaleString("it-IT", {maximumFractionDigits: 1});
const date = (value) => (value ? moment(value).format("DD/MM") : "—");
const playerName = (player) =>
	`${player.lastName || ""} ${player.firstName || ""}`.trim();
const sideIcon = (side) =>
	side === "home" ? (
		<HomeOutlinedIcon fontSize="inherit" titleAccess="In casa" />
	) : (
		<FlightIcon fontSize="inherit" titleAccess="In trasferta" />
	);
function PlayerLink({player}) {
	return <Link to={"/players/" + player.id}>{playerName(player)}</Link>;
}
function Detail({title, summary, children}) {
	return (
		<details className="md-page__detail">
			<summary>
				<strong>{title}</strong>
				<span>{summary}</span>
			</summary>
			<div className="md-page__detail-content">{children}</div>
		</details>
	);
}
function MatchDayContent({roster, season, handleNotification}) {
	const navigate = useNavigate();
	const lineupRef = useRef(null);
	// Moduli e scadenze per il campo condiviso con il dettaglio squadra.
	const [modules, setModules] = useState([]);
	const [contracts, setContracts] = useState({});
	useEffect(() => {
		let active = true;
		ModuleAPI.list()
			.then((response) => active && setModules(response || []))
			.catch(() => {});
		ContractAPI.expiring(36500)
			.then((response) => {
				if (!active) return;
				const map = {};
				(response || []).forEach((c) => {
					if (c.playerId) map[c.playerId] = c;
				});
				setContracts(map);
			})
			.catch(() => {});
		return () => {
			active = false;
		};
	}, []);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [retry, setRetry] = useState(0);
	useEffect(() => {
		let active = true;
		const load = async () => {
			setLoading(true);
			setData(null);
			setError(false);
			const now = moment();
			const weekStart = moment.max(
				moment(now).subtract(6, "days").startOf("day"),
				moment(season.startDate),
			);
			const monthStart = moment.max(
				moment(now).subtract(27, "days").startOf("day"),
				moment(season.startDate),
			);
			const measurementFilters = {
				rosterId: roster.id,
				seasonId: season.id,
				fromDate: weekStart.format("YYYY-MM-DD"),
				toDate: now.format("YYYY-MM-DD"),
			};
			try {
				const results = await Promise.allSettled([
					RosterAPI.getById(roster.id),
					MatchAPI.list({
						seasonId: season.id,
						fromDate: season.startDate,
						toDate: season.endDate,
						participantRosterId: roster.id,
						includeDetails: false,
					}),
					MedicalAPI.recordsList(),
					TrainingAPI.list({
						rosterId: roster.id,
						seasonId: season.id,
						fromDate: monthStart.toISOString(),
						toDate: now.toISOString(),
					}),
					PlayerAPI.listTQR(measurementFilters),
					PlayerAPI.listRPE({...measurementFilters, includePreparation: false}),
				]);
				if (!active) return;
				if (results[0].status !== "fulfilled")
					throw new Error("Rosa non disponibile");
				const value = (index) =>
					results[index].status === "fulfilled"
						? results[index].value || []
						: [];
				const team = value(0);
				const players = Array.from(
					new Map(
						(team.players || [])
							.map((entry) => entry.player)
							.filter((player) => player && isInternalPlayer(player))
							.map((player) => [player.id, player]),
					).values(),
				).sort((a, b) => playerName(a).localeCompare(playerName(b), "it"));
				const {next, past} = rosterMatches(value(1), roster.id, now);
				const medicalAvailable = results[2].status === "fulfilled";
				const {readiness, injured} = readinessForPlayers(
					players,
					value(2),
					value(4),
					value(5),
					now,
					medicalAvailable,
				);
				const trainings = rosterTrainings(value(3), roster.id, now).filter(
					(training) => moment(training.startDate).isSameOrAfter(monthStart),
				);
				const {attendance, weekly} = trainingSummary(trainings, players, now);
				const statisticsResponses = await Promise.allSettled(
					past.map((match) => MatchAPI.getPlayerStatistics(match.id)),
				);
				if (!active) return;
				const statistics = Object.fromEntries(
					past.map((match, index) => [
						match.id,
						statisticsResponses[index].status === "fulfilled"
							? statisticsResponses[index].value || []
							: [],
					]),
				);
				const failed = results
					.map((result, index) =>
						result.status === "rejected"
							? ["Rosa", "Partite", "Dati medici", "Allenamenti", "TQR", "RPE"][
									index
								]
							: null,
					)
					.filter(Boolean);
				if (
					statisticsResponses.some((response) => response.status === "rejected")
				)
					failed.push("Minuti di alcune gare");
				let wyscout = null;
				if (next) {
					try {
						const side = matchSide(next, roster.id);
						const opponent = side === "home" ? next.awayClub : next.homeClub;
						const saved = JSON.parse(
							localStorage.getItem("wyscout_team_stats") || "null",
						);
						const matches = opponent
							? (saved?.matches || []).filter((match) =>
									(match.Partita || "")
										.toLowerCase()
										.includes(opponent.toLowerCase()),
								)
							: [];
						if (matches.length) wyscout = {opponent, matches};
					} catch {
						/* Dati locali del fornitore facoltativi. */
					}
				}
				setData({
					team,
					players,
					readiness,
					injured,
					medicalAvailable,
					next,
					past,
					weekly,
					attendance,
					trainings,
					minutes: minutesSummary(players, past, statistics),
					failed,
					wyscout,
					weekStart,
					monthStart,
					asOf: now,
				});
			} catch {
				if (active) setError(true);
			} finally {
				if (active) setLoading(false);
			}
		};
		load();
		return () => {
			active = false;
		};
	}, [roster.id, season.id, season.startDate, season.endDate, retry]);
	const refresh = (
		<button
			type="button"
			className="md-page__retry"
			onClick={() => setRetry((value) => value + 1)}
		>
			Riprova
		</button>
	);
	if (loading)
		return (
			<>
				<Loading visible />
				<p className="md-page__muted" role="status">
					Caricamento dati della rosa...
				</p>
			</>
		);
	if (error || !data)
		return <p role="alert">Impossibile caricare la rosa. {refresh}</p>;
	const {
		team,
		players,
		readiness,
		injured,
		next,
		past,
		weekly,
		attendance,
		minutes,
		failed,
		medicalAvailable,
		wyscout,
	} = data;
	const readinessValues = Object.values(readiness);
	const categories = [
		["green", "Pronti"],
		["yellow", "Attenzione"],
		["red", "Non pronti"],
		["gray", "Dati insufficienti"],
	];
	const availableReadiness = readinessValues.filter(
		(item) => item.value != null,
	).length;
	const days = next
		? moment(next.startDate)
				.startOf("day")
				.diff(moment(data.asOf).startOf("day"), "days")
		: null;
	return (
		<>
			{failed.length > 0 && (
				<div className="md-page__warning" role="alert">
					Dati non disponibili: {failed.join(", ")}. I riepiloghi sono parziali.{" "}
					{refresh}
				</div>
			)}
			<div className="md-page__overview">
				<span>
					<small>Stagione</small>
					<strong>{season.name}</strong>
				</span>
				{next ? (
					<div className="md-page__next-match">
						<small>
							Prossima partita {days === 0 ? "· oggi" : `· tra ${days} giorni`}
						</small>
						<Link to={"/matches/" + next.id}>
							{sideIcon(matchSide(next, roster.id))}
							<strong>
								{next.homeTeamName || next.homeClub}{" "}
								<span className="md-page__vs">Vs</span>{" "}
								{next.awayTeamName || next.awayClub}
							</strong>
						</Link>
						<span>
							{moment(next.startDate).format("DD/MM/YYYY")} ·{" "}
							{next.startTimeToBeConfirmed ? (
								<abbr title="Orario da definire">OdD</abbr>
							) : (
								moment(next.startDate).format("HH:mm")
							)}
							{next.competitionSeasonId && (
								<>
									{" "}
									·{" "}
									<Link to={"/competitions/" + next.competitionSeasonId}>
										{next.competitionName || "Competizione"}
									</Link>
								</>
							)}
						</span>
					</div>
				) : (
					<span className="md-page__muted">
						{failed.includes("Partite")
							? "Calendario non disponibile"
							: "Nessuna prossima partita in calendario"}
					</span>
				)}
			</div>
			{!players.length ? (
				<p className="md-page__empty">
					Nessun giocatore interno assegnato alla rosa.
				</p>
			) : (
				<>
					<div className="md-page__readiness-strip">
						{categories.map(([color, label]) => (
							<div
								key={color}
								className={"md-page__kpi md-page__kpi--" + color}
							>
								<strong>
									{
										readinessValues.filter((item) => item.color === color)
											.length
									}
									<small>/{players.length}</small>
								</strong>
								<span>{label}</span>
							</div>
						))}
						<div className="md-page__kpi md-page__kpi--red">
							<strong>
								{medicalAvailable ? injured.size : "—"}
								<small>/{players.length}</small>
							</strong>
							<span>Infortunati</span>
						</div>
					</div>
					<p className="md-page__muted">
						Prontezza su {availableReadiness}/{players.length} giocatori ·
						RPE/TQR dal {date(data.weekStart)} al {date(data.asOf)}. Gli
						infortunati sono compresi nei non pronti. L’indice è indicativo, non
						un’idoneità medica.
					</p>
				</>
			)}
			<div className="md-page__workspace">
				<section className="md-page__panel md-page__formation">
					<div className="md-page__formation-head">
						<div>
							<h2>Formazione della rosa</h2>
							<p className="md-page__muted">
								La stessa del dettaglio squadra; convocazioni e distinta si
								gestiscono nella partita.
							</p>
						</div>
						<MainActions
							actions={[
								{
									icon: <PrintIcon />,
									label: "Stampa",
									title: "Stampa",
									onClick: () => lineupRef.current?.print(),
								},
							]}
						/>
					</div>
					<RosterLineup
						key={roster.id}
						ref={lineupRef}
						roster={{
							...team,
							players: (team.players || []).filter(
								(entry) => entry.player && isInternalPlayer(entry.player),
							),
						}}
						modules={modules}
						onOpenPlayer={(player) => navigate(getPlayerDetailPath(player))}
						handleNotification={handleNotification}
						contracts={contracts}
						showJerseyNumbers
					/>
				</section>
				<div className="md-page__side">
					<section className="md-page__panel">
						<h2>Ultime 5 partite</h2>
						{past.length ? (
							<div className="md-page__recent">
								{past.map((match) => (
									<Link
										key={match.id}
										to={"/matches/" + match.id}
										className={
											"md-page__match md-page__match--" +
											(match.outcome || "unknown")
										}
									>
										<span className="md-page__match-date">
											{date(match.startDate)} {sideIcon(match.side)}
										</span>
										<span className="md-page__opponent">
											{match.opponent}
											<small>
												{match.competitionName || "Competizione non indicata"}
											</small>
										</span>
										<span className="md-page__score">
											{match.score || "—"}
											<small>
												{match.conflict
													? "Da verificare"
													: !match.score
														? "Risultato non disponibile"
														: match.outcome === "V"
															? "Vittoria"
															: match.outcome === "S"
																? "Sconfitta"
																: "Pareggio"}
											</small>
										</span>
									</Link>
								))}
							</div>
						) : (
							<p className="md-page__muted">
								{failed.includes("Partite")
									? "Partite non disponibili."
									: "Nessuna gara conclusa disponibile."}
							</p>
						)}
					</section>
					<section className="md-page__panel">
						<h2>RPE medio degli allenamenti</h2>
						<p className="md-page__muted">
							Ultimi 7 giorni · solo presenti della rosa. Intensità su scala
							1–10.
						</p>
						{weekly.some((day) => day.avgRPE != null) ? (
							<div className="md-page__chart">
								<ResponsiveContainer width="100%" height={160}>
									<BarChart
										data={weekly}
										margin={{top: 8, right: 8, left: -28, bottom: 0}}
									>
										<CartesianGrid
											strokeDasharray="3 3"
											stroke={COLORS.border}
											vertical={false}
										/>
										<XAxis
											dataKey="label"
											tick={{fontSize: 11}}
											axisLine={false}
											tickLine={false}
										/>
										<YAxis
											domain={[0, 10]}
											tick={{fontSize: 11}}
											axisLine={false}
											tickLine={false}
										/>
										<Tooltip
											content={({active, payload}) =>
												active && payload?.[0] ? (
													<div className="md-page__tooltip">
														{payload[0].payload.date}
														<br />
														RPE medio: {number(payload[0].value)}
														<br />
														{payload[0].payload.measured}/
														{payload[0].payload.present} misure su presenze ·{" "}
														{payload[0].payload.sessions} sedute
													</div>
												) : null
											}
										/>
										<Bar
											dataKey="avgRPE"
											fill={COLORS.primary}
											radius={[3, 3, 0, 0]}
											maxBarSize={28}
										/>
									</BarChart>
								</ResponsiveContainer>
							</div>
						) : (
							<p className="md-page__empty">
								{failed.includes("Allenamenti")
									? "Allenamenti non disponibili."
									: "Nessuna rilevazione RPE disponibile nel periodo."}
							</p>
						)}
						{!failed.includes("Allenamenti") && (
							<p className="md-page__muted">
								{weekly.reduce((sum, day) => sum + day.sessions, 0)} sedute
								concluse · {weekly.reduce((sum, day) => sum + day.measured, 0)}/
								{weekly.reduce((sum, day) => sum + day.present, 0)} presenze con
								RPE. Giorni senza misure lasciati vuoti.
							</p>
						)}
					</section>
				</div>
			</div>
			<Detail
				title="Stato giocatori"
				summary={`${players.length} giocatori · ${availableReadiness} con indice`}
			>
				<p className="md-page__muted">
					Medie degli ultimi 7 giorni; date riferite all’ultima misura
					disponibile. Una sola fonte permette un indice parziale; le misure
					mancanti restano vuote.
				</p>
				<div className="md-page__table-wrap">
					<table>
						<thead>
							<tr>
								<th>Giocatore</th>
								<th>Ruolo</th>
								<th>
									RPE medio<small>ultima misura</small>
								</th>
								<th>
									TQR medio<small>ultima misura</small>
								</th>
								<th>Prontezza</th>
							</tr>
						</thead>
						<tbody>
							{players.map((player) => {
								const item = readiness[player.id];
								return (
									<tr key={player.id}>
										<td>
											<PlayerLink player={player} />
											{item.injured && (
												<span className="md-page__injury">Infortunato</span>
											)}
										</td>
										<td>{player.primaryRole || "—"}</td>
										<td title={`${item.rpeCount} misure`}>
											{number(item.avgRPE)}
											<small>{date(item.lastRpe)}</small>
										</td>
										<td title={`${item.tqrCount} misure`}>
											{number(item.avgTQR)}
											<small>{date(item.lastTqr)}</small>
										</td>
										<td>
											<span
												className={
													"md-page__index md-page__index--" + item.color
												}
												title={item.label}
											>
												{number(item.value)}
												{item.value != null ? "/100" : ""}
											</span>
											<small>
												{!medicalAvailable
													? "Dati medici non disponibili"
													: item.value == null
														? "Dati insufficienti"
														: !item.injured &&
															  (item.avgTQR == null || item.avgRPE == null)
															? "Manca TQR o RPE"
															: item.label}
											</small>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Detail>
			<Detail
				title="Impiego e presenze"
				summary={`${failed.includes("Partite") ? "Partite non disponibili" : `Ultime ${past.length} gare`} · ${failed.includes("Allenamenti") ? "Allenamenti non disponibili" : `${data.trainings.length} allenamenti negli ultimi 28 giorni`}`}
			>
				<div className="md-page__tables">
					<section>
						<h2>
							{failed.includes("Partite")
								? "Minuti non disponibili"
								: `Minuti nelle ultime ${past.length} gare`}
						</h2>
						<p className="md-page__muted">
							Solo minuti canonici disponibili. Lo zero è un dato rilevato; —
							indica assenza di dati. Nessuna presenza dedotta.
						</p>
						{failed.includes("Partite") ? (
							<p className="md-page__muted">Dati non disponibili.</p>
						) : (
							<table>
								<thead>
									<tr>
										<th>Giocatore</th>
										<th>Minuti</th>
										<th>Copertura</th>
									</tr>
								</thead>
								<tbody>
									{minutes.map((row) => (
										<tr key={row.player.id}>
											<td>
												<PlayerLink player={row.player} />
											</td>
											<td>{number(row.minutes)}</td>
											<td>
												{row.known}/{row.total}
												<small>
													{row.conflicts
														? `${row.conflicts} dati in conflitto`
														: row.known < row.total
															? "Dati parziali"
															: ""}
												</small>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</section>
					<section>
						<h2>Presenze agli allenamenti</h2>
						<p className="md-page__muted">
							Dal {date(data.monthStart)} al {date(data.asOf)} · solo sedute
							concluse di questa rosa. Rosa attuale; storico assegnazioni non
							ricostruito.
						</p>
						{failed.includes("Allenamenti") ? (
							<p className="md-page__muted">Dati non disponibili.</p>
						) : (
							<table>
								<thead>
									<tr>
										<th>Giocatore</th>
										<th>Presenze</th>
										<th>Da registrare</th>
									</tr>
								</thead>
								<tbody>
									{attendance.map((row) => (
										<tr key={row.player.id}>
											<td>
												<PlayerLink player={row.player} />
											</td>
											<td>
												{row.known ? row.present : "—"}/{row.total}
											</td>
											<td>{row.unknown || "—"}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</section>
				</div>
			</Detail>
			{wyscout && (
				<Detail
					title={"Dati importati vs " + wyscout.opponent}
					summary="Wyscout · dati locali di questo browser"
				>
					<p className="md-page__muted">
						Corrispondenze per nome dell’avversario da verificare. Questi dati
						non alimentano i riepiloghi della rosa.
					</p>
					{wyscout.matches.map((match, index) => (
						<p key={index}>
							{match.Partita} · Goal: {match.Goal ?? "—"} · Subiti:{" "}
							{match["Gol subiti"] ?? "—"} · Possesso:{" "}
							{match["Possesso palla, %"] ?? "—"}%
						</p>
					))}
				</Detail>
			)}
		</>
	);
}
export default function MatchDayPage({handleNotification}) {
	const season = useSelector((state) => state?.season);
	const clubId = getInternalClubId();
	const [rosterSet, setRosterSet] = useState(null);
	const [selectedId, setSelectedId] = useState(null);
	const [retry, setRetry] = useState(0);
	useEffect(() => {
		let active = true;
		setRosterSet(null);
		setSelectedId(null);
		if (!season?.id || !clubId)
			return () => {
				active = false;
			};
		RosterAPI.list({seasonId: season.id, clubId, prospect: false})
			.then((response) => {
				if (!active) return;
				const rosters = visibleRosters(
					(response || []).filter(
						(roster) =>
							!roster.prospect && !roster.isPool && roster.clubId === clubId,
					),
				);
				setRosterSet({seasonId: season.id, rosters});
				setSelectedId(rosters[0]?.id || null);
			})
			.catch(() => {
				if (active)
					setRosterSet({seasonId: season.id, rosters: [], error: true});
			});
		return () => {
			active = false;
		};
	}, [season?.id, clubId, retry]);
	const rosters = rosterSet?.seasonId === season?.id ? rosterSet.rosters : [];
	const selected = rosters.find((roster) => roster.id === selectedId);
	return (
		<Page>
			<Breadcrumbs elements={[]} current="Giorno gara" />
			<div className="md-page">
				<div className="md-page__header">
					<VLFSelectNew
						options={rosters.map((roster) => ({
							value: roster.id,
							label: roster.name,
						}))}
						sorted={false}
						isClearable={false}
						value={selected ? {value: selected.id, label: selected.name} : null}
						onChange={(option) => setSelectedId(option?.value || null)}
						placeholder="Seleziona rosa"
						containerStyles={{width: "min(100%, 280px)"}}
					/>
					{selected && (
						<Link
							to={"/manager/teams/" + selected.id}
							className="md-page__roster-link"
						>
							Apri rosa
						</Link>
					)}
				</div>
				{!season?.id ? (
					<p>Seleziona una stagione.</p>
				) : !clubId ? (
					<p>Nessun club associato alla sessione.</p>
				) : rosterSet?.error ? (
					<p role="alert">
						Impossibile caricare le rose.{" "}
						<button
							type="button"
							className="md-page__retry"
							onClick={() => setRetry((value) => value + 1)}
						>
							Riprova
						</button>
					</p>
				) : !rosterSet ? (
					<Loading visible />
				) : !rosters.length ? (
					<p>Nessuna rosa del tuo club abilitata nella stagione selezionata.</p>
				) : (
					selected && (
						<MatchDayContent
							key={season.id + "/" + selected.id}
							roster={selected}
							season={season}
							handleNotification={handleNotification}
						/>
					)
				)}
			</div>
		</Page>
	);
}
